import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { AlertTriangle, ShieldAlert, RotateCcw, PlayCircle, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtMoney(n) {
  const v = Number(n || 0);
  return (v < 0 ? "-" : "") + "$" + Math.abs(v).toLocaleString("en-US", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
}

export default function OYAKRepairPage() {
  const token = localStorage.getItem("st_token");
  const headers = { Authorization: `Bearer ${token}` };
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState("");
  const [typedConfirm, setTypedConfirm] = useState("");
  const [executeResult, setExecuteResult] = useState(null);
  const [lastAutoHeal, setLastAutoHeal] = useState(null);

  // Phase 41 (2026-07-28) — surface last auto-heal run so admin sees this
  // page is now the manual fallback, not the primary path.
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/auto-heal/events?limit=1`, { headers });
        if (r.data.events?.[0]) setLastAutoHeal(r.data.events[0]);
      } catch { /* non-blocking */ }
    })();
  }, []);

  async function runPreview() {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/admin/repair/oyak/preview`, {}, { headers });
      setPreview(r.data);
      toast.success(`Preview generated — ${r.data.candidate_count} candidate(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally { setLoading(false); }
  }

  async function runExecute() {
    if (typedConfirm !== "REPAIR OYAK LOANS") {
      toast.error('Type the exact phrase "REPAIR OYAK LOANS" to enable execution');
      return;
    }
    if (reason.trim().length < 20) {
      toast.error("Reason must be at least 20 characters");
      return;
    }
    if (!preview?.preview_hash) {
      toast.error("Run a Preview first");
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/admin/repair/oyak/execute`, {
        confirm: true,
        preview_hash: preview.preview_hash,
        reason,
      }, { headers });
      setExecuteResult(r.data);
      toast.success(`Repair executed — ${r.data.candidates_processed} candidate(s) processed in ${r.data.execution_time_ms}ms`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Execute failed");
    } finally { setLoading(false); }
  }

  const canExecute = !!preview && !!preview.preview_hash && typedConfirm === "REPAIR OYAK LOANS" && reason.trim().length >= 20;

  return (
    <div className="max-w-6xl mx-auto space-y-6 p-4" data-testid="oyak-repair-page">
      {/* Phase 41 chip — this manual repair is now the fallback, not the primary flow */}
      <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 flex items-center gap-3"
           data-testid="auto-heal-status-chip">
        <ShieldCheck className="h-5 w-5 text-emerald-700 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-sm text-emerald-800 font-medium">
            Now handled automatically by Auto-Heal
          </p>
          <p className="text-xs text-emerald-700 mt-0.5">
            {lastAutoHeal ? (
              <>Last run: {new Date(lastAutoHeal.ran_at).toLocaleString()} · repairs={lastAutoHeal.repairs_count} · escalated={lastAutoHeal.escalated_count}</>
            ) : (
              <>Cron runs every 15 minutes. No runs yet.</>
            )}
            {" · "}
            <Link to="/admin/auto-heal-log" className="underline hover:text-emerald-900"
                   data-testid="link-auto-heal-log">
              View log
            </Link>
          </p>
        </div>
      </div>

      <div className="rounded-md border-2 border-red-400 bg-red-50 p-4 flex gap-3 items-start"
           data-testid="oyak-repair-warning-banner">
        <AlertTriangle className="h-6 w-6 text-red-600 shrink-0" />
        <div>
          <div className="font-semibold text-red-800">Manual repair fallback</div>
          <div className="text-sm text-red-700">
            Auto-Heal already scans the OYAK path every 15 minutes. Use this page only if
            Auto-Heal has escalated the OYAK candidates for manual review or you need to
            re-run the repair against a specific hash. Every action is audit-logged and
            irreversible without rollback.
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PlayCircle className="h-5 w-5 text-sky-600" /> 1. Run Preview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Button onClick={runPreview} disabled={loading} data-testid="oyak-preview-btn"
                   className="bg-sky-600 hover:bg-sky-700 text-white">
            {loading ? "Running…" : preview ? "Re-run Preview" : "Run Preview (read-only)"}
          </Button>
          {preview && (
            <div className="mt-4 space-y-3 text-sm">
              <p><span className="font-medium">Backup:</span> <code className="text-xs">{preview.backup_file}</code> ({preview.backup_size_mb} MB)</p>
              <p><span className="font-medium">Candidates:</span> {preview.candidate_count}</p>
              <p><span className="font-medium">Preview hash:</span> <code className="text-xs">{preview.preview_hash?.slice(0, 24)}…</code></p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="font-semibold text-slate-700 mb-1">Current balances</div>
                  <table className="w-full text-xs" data-testid="oyak-current-balances">
                    <tbody>
                      {Object.entries(preview.current_balances).map(([k, v]) => (
                        <tr key={k}><td className="pr-2 text-slate-500">{k}</td><td className="text-right tabular-nums">{fmtMoney(v)}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div>
                  <div className="font-semibold text-slate-700 mb-1">Expected after repair</div>
                  <table className="w-full text-xs" data-testid="oyak-expected-balances">
                    <tbody>
                      {Object.entries(preview.expected_balances_after).map(([k, v]) => (
                        <tr key={k}><td className="pr-2 text-slate-500">{k}</td><td className="text-right tabular-nums">{fmtMoney(v)}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <details className="pt-2">
                <summary className="cursor-pointer text-slate-600">Reconciliation formula</summary>
                <pre className="bg-slate-50 text-[10px] p-2 mt-1 rounded whitespace-pre-wrap">{JSON.stringify(preview.reconciliation_check, null, 2)}</pre>
              </details>
              {preview.candidates.length > 0 && (
                <details className="pt-2" open>
                  <summary className="cursor-pointer text-slate-600">Candidate JEs ({preview.candidates.length})</summary>
                  <div className="mt-2 space-y-3">
                    {preview.candidates.map((c, i) => (
                      <div key={i} className="border rounded p-2 text-xs" data-testid={`oyak-candidate-${i}`}>
                        <div className="font-medium">{c.date} · ${c.amount?.toLocaleString()} · {c.narration}</div>
                        <div className="text-slate-600 italic mt-1">{c.reason}</div>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className={preview ? "" : "opacity-60 pointer-events-none"}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-red-600" /> 2. Execute Repair
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <label className="text-sm font-medium">Reason (min 20 chars)</label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)}
                       rows={3} placeholder="Reclassify OYAK shareholder-loan drawdowns from equity/cash to loan payable 2200 per audit finding …"
                       data-testid="oyak-reason-input" />
          </div>
          <div>
            <label className="text-sm font-medium">Type <code className="bg-slate-100 px-1">REPAIR OYAK LOANS</code> to enable execution</label>
            <Input value={typedConfirm} onChange={(e) => setTypedConfirm(e.target.value)}
                    placeholder="REPAIR OYAK LOANS"
                    data-testid="oyak-typed-confirm-input" />
          </div>
          <Button onClick={runExecute} disabled={!canExecute || loading}
                   className="bg-red-600 hover:bg-red-700 text-white"
                   data-testid="oyak-execute-btn">
            {loading ? "Executing…" : "Execute Repair (writes reversal + correction JEs)"}
          </Button>
          {executeResult && (
            <div className="mt-3 rounded border border-emerald-300 bg-emerald-50 p-3 text-sm" data-testid="oyak-execute-result">
              <div className="font-semibold text-emerald-800">Repair executed ✓</div>
              <div className="text-xs text-emerald-700 mt-1 space-y-1">
                <p>{executeResult.candidates_processed} candidate(s) processed in {executeResult.execution_time_ms}ms</p>
                <p>Backup: <code>{executeResult.backup_file}</code></p>
                <p>Audit log rows: {executeResult.audit_log_ids?.length}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-red-300">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-700">
            <RotateCcw className="h-5 w-5" /> 3. Emergency Rollback
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-slate-600">
            If the repair result is unexpected, run{" "}
            <code>POST /api/admin/repair/oyak/rollback</code> with the backup file path
            and the exact phrase <code>RESTORE OYAK BACKUP</code>. This restores
            transactions + journal_entries + audit_logs from the backup file.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
